/**
 * Assinatura do utilizador — documento em `users/{uid}/subscriptions/current`.
 * Campos migrados do documento raiz `users/{uid}` (legado fundido na leitura).
 */

import { z } from 'zod';

export const SUBSCRIPTION_DOC_ID = 'current' as const;

export const subscriptionSchema = z.object({
  id: z.literal(SUBSCRIPTION_DOC_ID),
  user_id: z.string().min(1),
  plan_id: z.string().uuid().nullable(),
  stripe_customer_id: z.string().min(1).nullable().optional(),
  stripe_subscription_id: z.string().min(1).nullable().optional(),
  subscription_status: z.string().min(1).nullable().optional(),
  subscription_valid_until: z.string().min(1).nullable().optional(),
  subscription_cancel_at_period_end: z.boolean().default(false),
  created_at: z.string(),
  updated_at: z.string(),
});

export type Subscription = z.infer<typeof subscriptionSchema>;

function tryParseUuidString(value: string): string | null {
  const t = value.trim();
  if (t.length === 0) return null;
  const parsed = z.string().uuid().safeParse(t);
  return parsed.success ? parsed.data : null;
}

function normalizePlanIdField(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    return tryParseUuidString(raw);
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const ref = raw as Record<string, unknown>;
  const idRaw = ref.id;
  if (typeof idRaw === 'string') {
    const fromId = tryParseUuidString(idRaw);
    if (fromId) return fromId;
  }
  const pathRaw = ref.path;
  if (typeof pathRaw === 'string') {
    const last = pathRaw.split('/').pop();
    if (last) {
      const fromPath = tryParseUuidString(last);
      if (fromPath) return fromPath;
    }
  }
  return null;
}

function coalesceNullableString(v: unknown): string | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t.length === 0 ? null : t;
}

/** Chaves de assinatura ainda possíveis em legado em `users/{uid}`. */
export const LEGACY_SUBSCRIPTION_FIELDS_ON_USER = [
  'plan_id',
  'stripe_customer_id',
  'stripe_subscription_id',
  'subscription_status',
  'subscription_valid_until',
  'subscription_cancel_at_period_end',
] as const;

/**
 * Extrai campos de assinatura de um snapshot legado do utilizador (para merge na leitura).
 */
export function legacySubscriptionPatchFromUserRaw(
  raw: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!raw) return {};
  const out: Record<string, unknown> = {};
  for (const k of LEGACY_SUBSCRIPTION_FIELDS_ON_USER) {
    if (k in raw) out[k] = raw[k];
  }
  return out;
}

/**
 * Mescla documento da subcoleção com campos legados no utilizador (subcoleção prevalece).
 */
export function mergeSubscriptionRaw(
  userId: string,
  subscriptionDoc: Record<string, unknown> | undefined,
  userDoc: Record<string, unknown> | undefined
): Record<string, unknown> {
  const legacy = legacySubscriptionPatchFromUserRaw(userDoc);
  const sub = subscriptionDoc ?? {};
  return {...legacy, ...sub};
}

/**
 * Normaliza dados para `subscriptionSchema.parse` após `mergeSubscriptionRaw`.
 */
export function normalizeSubscriptionFields(
  userId: string,
  merged: Record<string, unknown>
): Record<string, unknown> {
  const nowIso = new Date().toISOString();
  const created_at =
    typeof merged.created_at === 'string' && merged.created_at.trim().length > 0
      ? merged.created_at.trim()
      : nowIso;
  const updated_at =
    typeof merged.updated_at === 'string' && merged.updated_at.trim().length > 0
      ? merged.updated_at.trim()
      : nowIso;

  return {
    ...merged,
    id: SUBSCRIPTION_DOC_ID,
    user_id: typeof merged.user_id === 'string' && merged.user_id.trim().length > 0 ? merged.user_id : userId,
    plan_id: normalizePlanIdField(merged.plan_id),
    created_at,
    updated_at,
    stripe_customer_id: coalesceNullableString(merged.stripe_customer_id),
    stripe_subscription_id: coalesceNullableString(merged.stripe_subscription_id),
    subscription_status: coalesceNullableString(merged.subscription_status),
    subscription_valid_until: coalesceNullableString(merged.subscription_valid_until),
    subscription_cancel_at_period_end:
      typeof merged.subscription_cancel_at_period_end === 'boolean'
        ? merged.subscription_cancel_at_period_end
        : false,
  };
}

/** Referências Firestore da subcoleção de assinatura. */
export class SubscriptionRef {
  static readonly collection = 'subscriptions';

  static docPath(userId: string): string {
    return `users/${userId}/${this.collection}/${SUBSCRIPTION_DOC_ID}`;
  }
}
